import { isHuafenda24GKeyboard } from "../main/deviceConnectionUtils";
import {
  resolveHuafendaKeyboardTypeFromNameBlob,
  resolveSupportedKeyboardTypeForEquipment,
  resolveHuafendaConfigRowForEquipment,
  resolveHuafendaConfigRowByNameAndWorkMode,
  resolveHuafendaWirelessConfigRowForEquipment,
  huafendaIdentifiersCompatible,
  normHuafendaIdentifier,
} from "./supportedKeyboardMatch";
import { isHfd24GDongleProductId } from "./hfdConnectHidResolve";

/** 华奋达 getDeviceInfo 中与灯光/改键协议相关的字段 */
export type HfdSdkRuntimeSnapshot = {
  workMode?: number;
  lightingVersion?: number;
  frameVersion?: number;
  identifier?: string;
  product?: string | number;
};

export function resolveHfdIsUsbDevice(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment) return true;
  return !isHuafenda24GKeyboard(equipment);
}

/**
 * X87 V2 等 0xfef9 接收器：无 SDK 运行时字段时默认走 lightingVersion=1 新 64 字节律动。
 * X87 / SMART875（0xfefe）仍回落 0，与已验收首页路径一致。
 */
export function isHfd24GNewProtocolRhythmTarget(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment) return false;
  const pid = Number(equipment.productId);
  if (pid === 0xfef9) return true;
  const id = String(equipment.identifier ?? "").toLowerCase();
  if (id.includes("65273")) return true;
  const raw =
    equipment.hfdLightingVersion ??
    equipment.lightingVersion ??
    (equipment as { hfdDeviceInfo?: HfdSdkRuntimeSnapshot }).hfdDeviceInfo
      ?.lightingVersion;
  return Number(raw) === 1;
}

/** SDK lightingVersion：0 旧无线灯效/律动，1 启用 2.4G 64 字节新协议 */
export function resolveHfdLightingVersion(
  equipment: Record<string, unknown> | null | undefined
): number {
  if (!equipment) return 0;
  const raw =
    equipment.hfdLightingVersion ??
    equipment.lightingVersion ??
    (equipment as { hfdDeviceInfo?: HfdSdkRuntimeSnapshot }).hfdDeviceInfo
      ?.lightingVersion;
  const n = Number(raw);
  if (n === 1) return 1;
  if (
    isHuafenda24GKeyboard(equipment) &&
    isHfd24GNewProtocolRhythmTarget(equipment)
  ) {
    return 1;
  }
  return 0;
}

export function resolveHfdMusicDataSendParams(
  equipment: Record<string, unknown> | null | undefined
): { isUSBDevice: boolean; lightingVersion: number } {
  return {
    isUSBDevice: resolveHfdIsUsbDevice(equipment),
    lightingVersion: resolveHfdLightingVersion(equipment),
  };
}

export type MergeHfdSdkRuntimeOptions = {
  /** 点卡片/子应用会话锚点；SDK 返回值不一致时不覆盖 */
  preserveIdentifier?: string;
};

export function mergeHfdSdkRuntimeIntoEquipment<
  T extends Record<string, unknown>,
>(
  equipment: T,
  deviceInfo: HfdSdkRuntimeSnapshot | null | undefined,
  options?: MergeHfdSdkRuntimeOptions
): T {
  if (!deviceInfo) return equipment;
  const next: Record<string, unknown> = { ...equipment };
  if (deviceInfo.workMode !== undefined && deviceInfo.workMode !== null) {
    next.workMode = deviceInfo.workMode;
  }
  if (deviceInfo.lightingVersion !== undefined) {
    next.hfdLightingVersion = deviceInfo.lightingVersion;
  }
  if (deviceInfo.frameVersion !== undefined) {
    next.hfdFrameVersion = deviceInfo.frameVersion;
  }
  if (deviceInfo.identifier) {
    const sdkId = normHuafendaIdentifier(deviceInfo.identifier);
    const anchor = normHuafendaIdentifier(options?.preserveIdentifier);
    if (anchor && sdkId && sdkId !== anchor) {
      // 裸 id ↔ SDK 带 :workMode:product：包含即兼容，取更长（通常为 SDK 全量 id）
      if (huafendaIdentifiersCompatible(anchor, sdkId)) {
        next.identifier = sdkId.length >= anchor.length ? sdkId : anchor;
      } else {
        console.warn("[HFD] SDK identifier 与会话锚点不一致，保留锚点", {
          anchor,
          sdkId,
        });
      }
    } else if (sdkId) {
      next.identifier = sdkId;
    }
  }
  if (deviceInfo.product !== undefined && deviceInfo.product !== null) {
    next.product = deviceInfo.product;
  }
  return next as T;
}

/**
 * 2.4G（含 lightingVersion=1 新 64 字节协议）：musicData 须与键盘配置遍历序一致。
 * - 旧版：按数组下标映射灯位（keyIndex 可忽略）
 * - 新版：按出现顺序读取 [keyIndex,r,g,b] 转 RGB565
 * 有线 USB：按 keyIndex 排序组包（64 字节协议）。
 */
export function shouldUseHfdRhythmLayoutOrder(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  return isHuafenda24GKeyboard(equipment);
}

/** 灯光/改键/虚拟键盘：displayname 型号名优先（SMART875→84，X87→huafenda87） */
export function resolveHfdKeyboardTypeFromEquipment(
  equipment: Record<string, unknown> | null | undefined
): string {
  const fromName = resolveHuafendaKeyboardTypeFromNameBlob(equipment);
  if (fromName) return fromName;
  const resolved = resolveSupportedKeyboardTypeForEquipment(equipment);
  if (resolved) return resolved;
  const raw = String(equipment?.keyboardType ?? "").trim();
  return raw && raw !== "undefined" && raw !== "null" ? raw : "";
}

/** 子应用进入时校正 equipmentData（keyboardType / 2.4G 标记），避免共用 dongle 落错型号 */
export function normalizeHuafendaEquipmentForSubApp<
  T extends Record<string, unknown>,
>(equipment: T): T {
  if (!equipment || String(equipment.solutionRovider ?? "") !== "huafenda") {
    return equipment;
  }
  const next: Record<string, unknown> = { ...equipment };
  const cardDisplay =
    next.displayname ?? (next as { displayName?: unknown }).displayName;
  const cardIdentSnapshot = normHuafendaIdentifier(next.identifier);
  const fromNameKt = resolveHuafendaKeyboardTypeFromNameBlob(next);

  let configRow: Record<string, unknown> | null = fromNameKt
    ? resolveHuafendaConfigRowByNameAndWorkMode(next)
    : null;
  if (!configRow) {
    configRow = resolveHuafendaConfigRowForEquipment(next);
  }
  if (fromNameKt && configRow) {
    const rowKt = String(configRow.keyboardType ?? "");
    if (rowKt !== fromNameKt) {
      const byName = resolveHuafendaConfigRowByNameAndWorkMode(next);
      if (byName) configRow = byName;
    }
  }

  if (configRow) {
    if (configRow.keyboardType) next.keyboardType = configRow.keyboardType;
    if (configRow.productName) next.productName = configRow.productName;
    if (configRow.is4G === true) next.is4G = true;
    if (configRow.workMode != null && configRow.workMode !== "") {
      next.workMode = configRow.workMode;
    }
    if (configRow.productId != null) {
      next.productId = configRow.productId;
    }
    if (configRow.displayname && !cardDisplay) {
      next.displayname = configRow.displayname;
    }
    if (configRow.identifier && !cardIdentSnapshot) {
      next.identifier = configRow.identifier;
    }
    // 磁轴标记：battle68 等与机械轴菜单/协议分支
    if (configRow.magneticAxis === true) {
      next.magneticAxis = true;
    } else if (configRow.magneticAxis === false) {
      next.magneticAxis = false;
    }
  }

  if (cardDisplay != null && String(cardDisplay).trim()) {
    next.displayname = cardDisplay;
  }

  const keyboardType = resolveHfdKeyboardTypeFromEquipment(next);
  if (keyboardType) {
    next.keyboardType = keyboardType;
  }

  const wm = next.workMode;
  const is24G =
    wm === 2 ||
    wm === "2" ||
    next.is4G === true ||
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("huafenda2.4G") === "true");

  if (is24G) {
    next.is4G = true;
    if (wm === undefined || wm === null || wm === "") {
      next.workMode = "2";
    }
    const pid = Number(next.productId);
    const isWiredPid =
      Number.isFinite(pid) &&
      !isHfd24GDongleProductId(pid) &&
      (pid === 0x8006 || pid === 0x80bb || String(next.workMode) === "0");
    if ((isWiredPid || !isHfd24GDongleProductId(pid)) && keyboardType) {
      const wireless = resolveHuafendaWirelessConfigRowForEquipment(
        next,
        keyboardType
      );
      if (wireless?.productId != null) {
        next.productId = wireless.productId;
      }
      if (wireless?.identifier) {
        next.identifier = wireless.identifier;
      } else if (cardIdentSnapshot) {
        next.identifier = cardIdentSnapshot;
      }
    }
  }

  if (cardIdentSnapshot) {
    const rowForCard = resolveHuafendaConfigRowByNameAndWorkMode(next);
    const expectedIdent = normHuafendaIdentifier(
      rowForCard?.identifier ?? configRow?.identifier
    );
    if (
      !expectedIdent ||
      cardIdentSnapshot === expectedIdent ||
      cardIdentSnapshot.toLowerCase().includes("smart875") ||
      cardIdentSnapshot.toLowerCase().includes("x87") ||
      cardIdentSnapshot.toLowerCase().includes("x75gc")
    ) {
      next.identifier = cardIdentSnapshot;
    }
  }

  return next as T;
}
